import { makeAutoObservable } from 'mobx';

import {
  computeAggregateMetrics,
  filterSnapshots,
  getAvailableApps,
  type CkSnapshotFilters,
} from './ck-debug.utils';

import type { CkAggregateMetrics, CkSnapshot } from './ck-debug.types';

const DEFAULT_FILTERS: CkSnapshotFilters = {
  app: '',
  trigger: '',
  outcome: '',
  searchTerm: '',
};

function createDefaultFilters(): CkSnapshotFilters {
  return { ...DEFAULT_FILTERS };
}

class CkDebugStore {
  snapshots: CkSnapshot[] = [];
  selectedSnapshotId: string | null = null;
  pageActive = false;
  filters = createDefaultFilters();

  private readonly MAX_ACTIVE = 500;
  private readonly MAX_INACTIVE = 50;

  constructor() {
    makeAutoObservable(this);
  }

  get currentLimit(): number {
    return this.pageActive ? this.MAX_ACTIVE : this.MAX_INACTIVE;
  }

  get filteredSnapshots(): CkSnapshot[] {
    return filterSnapshots(this.snapshots, this.filters);
  }

  get availableApps(): { name: string; count: number }[] {
    return getAvailableApps(this.snapshots);
  }

  get aggregateMetrics(): CkAggregateMetrics {
    return computeAggregateMetrics(this.filteredSnapshots);
  }

  get selectedSnapshot(): CkSnapshot | undefined {
    if (!this.selectedSnapshotId) return undefined;
    return this.snapshots.find((s) => s.jobId === this.selectedSnapshotId);
  }

  addSnapshot(payload: Omit<CkSnapshot, 'receivedAt'>) {
    this.snapshots.unshift({ ...payload, receivedAt: Date.now() });
    this.trimToLimit();
  }

  enterPage() {
    this.clear();
    this.resetFilters();
    this.pageActive = true;
  }

  leavePage() {
    this.clear();
    this.resetFilters();
    this.pageActive = false;
  }

  setFilters(partial: Partial<CkSnapshotFilters>) {
    Object.assign(this.filters, partial);
  }

  selectSnapshot(id: string | null) {
    this.selectedSnapshotId = id;
  }

  clear() {
    this.snapshots = [];
    this.selectedSnapshotId = null;
  }

  resetFilters() {
    this.filters = createDefaultFilters();
  }

  trimToLimit() {
    if (this.snapshots.length > this.currentLimit) {
      this.snapshots = this.snapshots.slice(0, this.currentLimit);
      if (
        this.selectedSnapshotId &&
        !this.snapshots.find((s) => s.jobId === this.selectedSnapshotId)
      ) {
        this.selectedSnapshotId = null;
      }
    }
  }
}

export const ckDebugStore = new CkDebugStore();
